import React, { useMemo, useRef, useState } from 'react';
import { useChipPresent } from 'components/CellAnnotationValueViewer/components/MoneyChipValueViewer';
import { usePatchCapTableSecurities } from 'services/hooks/captable';
import { useReviewDrawerOpen, useReviewItemStatus } from 'store/useReviewStore';
import CitationValueViewerProps from './CitationValueViewer.props';
import {
  citationBadgeSx,
  StyledCitationNumber,
  StyledCitationNumberWrapper,
  StyledCitationValueViewer,
  StyledCitationValueViewerWrapper,
} from './CitationValueViewer.styles';
import { hasInlineControl } from './inlineControlLayout';
import CitationPopover from '../../../../../components/Popovers/citation-popover/CitationPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { EventType, useEventBus } from '../../../../../context';
import { useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';
import {
  citationReviewStatus,
  isAiCreatedCitation,
  pickActiveCitation,
  reviewStatusOf,
  statusToHighlightVariant,
} from '../../utilities/reviewAggregator';
import { removeCitationFromSecurities } from '../../utilities/securityAiPatches';
import { reviewPendingTextSx } from '../ai-security-finding-popover/AIMergeValueViewer.styles';
import { useCitationContext } from '../captable-table/CitationContext';

const CitationValueViewer = ({
  value,
  citationReferences,
  citationNumber,
  originalValueViewer: OriginalValueViewer,
  citation,
  ...restProps
}: CitationValueViewerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredCitation, setHoveredCitation] = useState<CitationReferenceData | undefined>(undefined);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setActiveCitation, activeCitation } = useCitationContext();
  const { publish } = useEventBus();
  const { getSourceNumber } = useDocumentFootnotes();
  const patchSecurities = usePatchCapTableSecurities();

  // When the cell also renders a Pre-FX currency chip (cap-table OIP), the chip
  // is the leftmost element; shift the citation footnote to its right so the
  // two don't overlap (SCAL-9062). 36px clears the fixed-size chip (~28px) plus
  // the layout gap. The chip reveals on hover OR selection, so pass the same
  // hovered-or-selected signal here to engage the offset exactly when it shows.
  // The hook is called unconditionally at the top level (React hooks rules).
  const chipPresent = useChipPresent(restProps.cell, isHovered || Boolean(restProps.selected));
  const citationOffsetLeft = chipPresent ? 36 : 2;

  // Grid-visible citations (SCAL-8969): a DISMISSED citation never renders a
  // footnote badge - it stays reachable (grayed) from popover/review surfaces only.
  // ONE badge per DOCUMENT: footnote numbers are keyed by document_reference, so
  // duplicate rows citing the same document collapse to the first visible row -
  // badge count always equals distinct source numbers.
  const visibleCitations = useMemo(() => {
    const firstPerDocument = new Map<number, CitationReferenceData>();
    (restProps.cell.citationReferences ?? []).forEach(cit => {
      if (citationReviewStatus(cit) !== 'dismissed' && !firstPerDocument.has(cit.document_reference)) {
        firstPerDocument.set(cit.document_reference, cit);
      }
    });
    return [...firstPerDocument.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citationReferences]);

  // visibleCitations is already one row per document, so the numbers are distinct.
  const citationSourceNumbers = useMemo(
    () =>
      visibleCitations
        .map(cit => getSourceNumber(cit.document_reference))
        .filter((sourceNumber): sourceNumber is number => sourceNumber !== undefined)
        .sort((a, b) => a - b),
    [visibleCitations, getSourceNumber]
  );

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => {
      setHoveredCitation(undefined);
      setAnchorEl(null);
    }, 120);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const titleCell = useMemo(() => {
    const columnId = restProps.cell.columnLegend;
    const titleCellForColumn = restProps.cell.sheet?.cells?.[`${columnId}1`];
    return titleCellForColumn?.value ? (titleCellForColumn.value as string) : 'Unknown...';
  }, [restProps.cell]);

  const handleBadgeClick = (e: React.MouseEvent<HTMLElement>, cit: CitationReferenceData) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setHoveredCitation(cit);
  };

  const handleCitationDelete = (citationId: number) => {
    setHoveredCitation(undefined);
    setAnchorEl(null);
    setActiveCitation(undefined);
    setIsHovered(false);

    patchSecurities(securities => removeCitationFromSecurities(securities, citationId) as typeof securities);
  };

  const citationContainsActive = useMemo(() => {
    const refForActive = citationReferences?.find(cit => cit.id === activeCitation?.id);

    return Boolean(refForActive);
  }, [citationReferences, activeCitation]);

  // Dropdown and date-picker cells own the cell interior, so the badge cannot
  // overlay them. Reserve a stable left gutter sized to the badges (so the
  // indicator stays usable and the layout never jumps) instead (SCAL-8987).
  const reservedBadgeWidth = useMemo(() => {
    if (!hasInlineControl(restProps.cell)) {
      return 0;
    }
    const badgeCount = Math.max(1, citationSourceNumbers.length);
    const perBadge = citationSourceNumbers.length > 1 ? 40 : 24;
    // badges + 2px gaps between them + the 2px left offset and a small margin
    return badgeCount * perBadge + (badgeCount - 1) * 2 + 6;
  }, [restProps.cell, citationSourceNumbers.length]);

  // SCAL-9150 completion: while the review drawer is open, ANY cell whose review item
  // is still PENDING paints its text AI-purple at rest - including citation-only cells
  // (no AI field suggestion), which this viewer alone wraps and which previously read
  // plain. The store item is the SAME source the side panel renders from (the panel
  // flips it on accept/decline), so cell and card can never disagree; the fallback
  // mirrors the aggregator's own derivation - field metadata status when present, the
  // ACTIVE citation's status otherwise - guarded by the same (columnId, alias)
  // eligibility, for the moments before an item lands in the store. Cells with field
  // metadata are also tinted by their outer AIFinding/AIMerge wrapper; both apply the
  // identical scoped-gradient rule, so the overlap is a no-op.
  const drawerOpen = useReviewDrawerOpen();
  const { columnId, alias } = restProps.cell;
  const storeStatus = useReviewItemStatus(columnId != null ? Number(columnId) : undefined, alias);
  const localStatus = restProps.cell.aiFieldMetadata != null
    ? reviewStatusOf(restProps.cell.aiFieldMetadata)
    : citationReviewStatus(pickActiveCitation(restProps.cell.citationReferences));
  const itemStatus = storeStatus ?? (columnId != null && alias ? localStatus : undefined);
  const restIsPurple = drawerOpen && itemStatus === 'pending';

  // SCAL-8969: a DISMISSED field shows NOTHING AI-related on the grid - the citation
  // footnote reverts to a plain cell (the dismissed citation stays reachable only from
  // the review panel). Reuse the field-level review status the grid already keys on
  // (aiFieldMetadata.status); accepted/pending keep their footnote. The same applies
  // per citation: when every citation on the cell is itself dismissed, nothing renders.
  const isDismissed = restProps.cell?.aiFieldMetadata?.status === 'dismissed';

  if (isDismissed || visibleCitations.length === 0) {
    // No citations (or dismissed): render original viewer or plain text
    if (OriginalValueViewer) {
      return <OriginalValueViewer value={value} {...restProps} />;
    }
    return <FastValueViewer value={value} {...restProps} />;
  }

  return (
    <StyledCitationValueViewerWrapper
      onMouseEnter={() => {
        cancelClose();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        scheduleClose();
        setIsHovered(false);
      }}>
      {(isHovered || Boolean(hoveredCitation) || citationContainsActive) && (
        <StyledCitationNumberWrapper offsetLeft={citationOffsetLeft}>
          {visibleCitations.map(cit => {
            const status = citationReviewStatus(cit);
            const aiCreated = isAiCreatedCitation(cit);

            return (
              <StyledCitationNumber
                key={cit.id}
                data-testid="citation-badge"
                data-review-status={status}
                data-ai-created={aiCreated || undefined}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => handleBadgeClick(e, cit)}
                sx={citationBadgeSx({
                  pending: status === 'pending',
                  aiCreated,
                  active: hoveredCitation?.id === cit.id || activeCitation?.id === cit.id,
                  wide: citationSourceNumbers.length > 1,
                })}>
                {getSourceNumber(cit.document_reference)}
              </StyledCitationNumber>
            );
          })}
        </StyledCitationNumberWrapper>
      )}

      <StyledCitationValueViewer
        reservedBadgeWidth={reservedBadgeWidth}
        data-review-purple={restIsPurple || undefined}
        sx={restIsPurple ? reviewPendingTextSx : undefined}>
        {OriginalValueViewer ? (
          <OriginalValueViewer value={value} {...restProps} />
        ) : (
          <FastValueViewer className="text-cell" value={value} {...restProps} />
        )}
      </StyledCitationValueViewer>

      {hoveredCitation && (
        <CitationPopover
          onView={cit => {
            // Highlight color follows the CITATION'S review status (SCAL-8986), not
            // the navigation source: a still-pending citation traced from the cell
            // footnote reads AI-purple, an accepted/manual one plain yellow.
            setActiveCitation(cit, statusToHighlightVariant(citationReviewStatus(cit)));
            publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
              documentReferenceId: cit.document_reference,
              expandDrawer: true,
              activeCitationData: cit,
            });
          }}
          open={Boolean(hoveredCitation)}
          anchorEl={anchorEl}
          onClose={scheduleClose}
          citation={hoveredCitation}
          columnarIdentifier={titleCell}
          onDelete={handleCitationDelete}
          slotProps={{
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
            },
          }}
        />
      )}
    </StyledCitationValueViewerWrapper>
  );
};

export default CitationValueViewer;
